/**
 * buildProductFormSchema.ts
 *
 * Yup schema factory for the Add / Edit Product form.
 * A single schema covers both create and edit; the only structural difference
 * is whether `storeId` is required (company-admin create flow) and whether
 * `categoryId` and `brandName` are required (create) or optional (edit, to
 * support legacy products that pre-date those fields).
 *
 * Character-level validation is delegated to productValidation utilities so
 * that the same rules apply consistently across all product/product-item forms.
 */
import * as Yup from "yup";
import { SellingMode } from "../enums/sellingMode";
import { Unit } from "../enums/unit";
import {
  ProductFormData,
  BuildProductFormSchemaOptions
} from "../types/products";
import {
  productNameYupTest,
  descriptionYupTest
} from "../../../Utils/productValidation";

export const buildProductFormSchema = (
  t: (key: string, fallback: string) => string,
  options: BuildProductFormSchemaOptions = {}
): Yup.ObjectSchema<ProductFormData> => {
  const { variant = "create", allowedSellingModes } = options;
  const sellingModeValues =
    allowedSellingModes && allowedSellingModes.length > 0
      ? [...allowedSellingModes]
      : Object.values(SellingMode);

  return Yup.object({
    // ── Core identity ────────────────────────────────────────────────────────
    name: Yup.string()
      .trim()
      .required(t("add-product-name-required", "Product name is required"))
      .min(
        2,
        t("add-product-name-min", "Product name must be at least 2 characters")
      )
      .max(
        200,
        t(
          "add-product-name-max",
          "Product name must be 200 characters or fewer"
        )
      )
      .test(
        productNameYupTest(t("add-product-product-name-label", "Product name"))
      ),

    brandName:
      variant === "create"
        ? Yup.string()
            .trim()
            .required(t("add-product-brand-required", "Brand name is required"))
            .min(
              1,
              t(
                "add-product-brand-min",
                "Brand name must be at least 1 character"
              )
            )
            .max(
              100,
              t(
                "add-product-brand-max",
                "Brand name must be 100 characters or fewer"
              )
            )
            .test(
              productNameYupTest(
                t("add-product-brand-name-label", "Brand name")
              )
            )
        : Yup.string()
            .trim()
            .max(
              100,
              t(
                "add-product-brand-max",
                "Brand name must be 100 characters or fewer"
              )
            )
            .test(
              productNameYupTest(
                t("add-product-brand-name-label", "Brand name")
              )
            ),

    description: Yup.string()
      .trim()
      .required(
        t("add-product-description-required", "Description is required")
      )
      .max(
        2000,
        t(
          "add-product-description-max",
          "Description must be 2000 characters or fewer"
        )
      )
      .test(
        descriptionYupTest(t("add-product-description-label", "Description"))
      ),

    // ── Selling configuration ────────────────────────────────────────────────
    sellingMode: Yup.mixed<SellingMode>()
      .oneOf(
        sellingModeValues as SellingMode[],
        t(
          "add-product-selling-mode-invalid",
          "Please select a valid selling mode"
        )
      )
      .required(
        t("add-product-selling-mode-required", "Selling mode is required")
      ),

    baseUnit: Yup.string().when("sellingMode", {
      is: SellingMode.MEASURED,
      then: (schema) =>
        schema
          .required(
            t(
              "add-product-base-unit-required",
              "A unit is required for measured products"
            )
          )
          .test(
            "measured-base-unit-not-piece",
            t(
              "add-product-measured-unit-not-piece",
              "Measured products must use a weight, volume, or length unit — not Unit (piece)."
            ),
            (unit) => unit !== undefined && unit !== Unit.UNIT
          ),
      otherwise: (schema) => schema.optional()
    }),

    isPartialSellingAllowed: Yup.boolean()
      .typeError(
        t(
          "add-product-partial-required",
          "Please specify whether partial selling is allowed"
        )
      )
      .required(
        t(
          "add-product-partial-required",
          "Please specify whether partial selling is allowed"
        )
      ),

    // ── Categorisation & supplier ────────────────────────────────────────────
    categoryId:
      variant === "create"
        ? Yup.string()
            .trim()
            .required(
              t("add-product-category-required", "Category is required")
            )
        : Yup.string().trim().optional(),

    supplierId: Yup.string().optional(),

    storeId: options.requireStoreForCompanyAdmin
      ? Yup.string()
          .trim()
          .required(t("add-product-store-required", "Store is required"))
      : Yup.string().optional()
  }) as unknown as Yup.ObjectSchema<ProductFormData>;
};
